

import React, { useState, createContext, useContext, useEffect } from "react";
import { Pencil, Trash, Plus, Search } from "lucide-react";
import axios from "axios";


const API_BASE = "https://api.propertygalleria.in/master/api";

/* ---------------- MOCK ADMIN CONTEXT ---------------- */

const AdminContext = createContext();

const AdminProvider = ({ children }) => {
    const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);


    /* -------- GET ALL ZONES -------- */
  const fetchZones = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/zones`);
      setZones(res.data);
    } catch (err) {
      console.error(err);
      alert("Failed to fetch zones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchZones();
  }, []);


  /* -------- ADD ZONE -------- */
  const addZone = async ({ description }) => {
    try {
      await axios.post(`${API_BASE}/api/zones`, {
        zone_desc: description,
      });
      fetchZones();
    } catch (err) {
      console.error(err);
      alert("Failed to create zone");
    }
  };

  /* -------- UPDATE ZONE -------- */
  const updateZone = async (zone_code, { description }) => {
    try {
      await axios.put(`${API_BASE}/api/zones/${zone_code}`, {
        zone_desc: description,
      });
      fetchZones();
    } catch (err) {
      console.error(err);
      alert("Failed to update zone");
    }
  };

  /* -------- DELETE ZONE -------- */
  const deleteZone = async (zone_code) => {
    try {
      await axios.delete(`${API_BASE}/api/zones/${zone_code}`);
      fetchZones();
    } catch (err) {
      console.error(err);
      alert("Failed to delete zone");
    }
  };


  return (
    <AdminContext.Provider value={{ zones, addZone, updateZone, deleteZone }}>
      {children}
    </AdminContext.Provider>
  );
};

const useAdmin = () => useContext(AdminContext);

/* ---------------- ZONE MASTER ---------------- */

const ZoneMaster = () => {
  const { zones, addZone, updateZone, deleteZone } = useAdmin();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [description, setDescription] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!description.trim()) {
      alert("Please enter zone description");
      return;
    }

   if (editingZone) {
  updateZone(editingZone.zone_code, { description });
} else {
  addZone({ description });
}


    resetForm();
  };

  const resetForm = () => {
    setDescription("");
    setEditingZone(null);
    setIsFormOpen(false);
  };

const handleEdit = (zone) => {
  setEditingZone(zone);
  setDescription(zone.zone_desc);
  setIsFormOpen(true);
};

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this zone?")) {
      deleteZone(id);
      alert("Zone deleted successfully");
    }
  };

const filteredZones = zones.filter((z) =>
  (z.zone_desc || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
  (z.zone_code || "").toLowerCase().includes(searchTerm.toLowerCase())
);


  return (
    <div style={{ padding: 24, fontFamily: "Arial" }}>
      <h2 style={{ color: "#0B1C3D" }}>Highway Master</h2>
      <p style={{ color: "#666" }}>
        Manage geographical zones for property allocation
      </p>

      {/* ACTION BAR */}
      <div style={{ display: "flex", marginBottom: 20, gap: 10 }}>
        <div style={{ position: "relative", flex: 1 }}>
          <Search
            size={18}
            style={{ position: "absolute", top: 10, left: 10, color: "#999" }}
          />
          <input
            type="text"
            placeholder="Search Highway..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "8px 8px 8px 35px",
              borderRadius: 6,
              border: "1px solid #ccc",
            }}
          />
        </div>

        <button
          onClick={() => setIsFormOpen(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "#8b306d",
            color: "#fff",
            padding: "8px 14px",
            borderRadius: 6,
            border: "none",
            cursor: "pointer",
          }}
        >
          <Plus size={18} /> Add Highway
        </button>
      </div>

      {/* FORM */}
      {isFormOpen && (
        <form
          onSubmit={handleSubmit}
          style={{
            border: "1px solid #ddd",
            padding: 16,
            borderRadius: 6,
            marginBottom: 20,
            background: "#fff",
          }}
        >
          <h3 style={{ marginBottom: 10 }}>
            {editingZone ? "Edit Highway" : "Create Highway"}
          </h3>

          <input
            type="text"
            placeholder="Highway description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{
              width: "100%",
              padding: 8,
              marginBottom: 12,
              borderRadius: 6,
              border: "1px solid #ccc",
            }}
          />

          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="submit"
              style={{
                background: "#8b306d",
                color: "#fff",
                padding: "8px 16px",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              {editingZone ? "Update" : "Create"}
            </button>

            <button
              type="button"
              onClick={resetForm}
              style={{
                background: "#eee",
                padding: "8px 16px",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* TABLE */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          background: "#fff",
        }}
      >
        <thead>
          <tr style={{ background: "#8b306d", color: "#fff" }}>
            <th style={th}>Highway ID</th>
            <th style={th}>Description</th>
            <th style={th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredZones.length === 0 ? (
            <tr>
              <td colSpan="3" style={{border: "1px solid #ddd", textAlign: "center" }}>
                No Highway found
              </td>
            </tr>
          ) : (
            filteredZones.map((zone, i) => (
              <tr key={zone.id} style={{ background: i % 2 ? "#f9f9f9" : "#fff" }}>
                <td style={td}>{zone.zone_code}</td>
                <td style={td}>{zone.zone_desc}</td>
                <td style={{ ...td, textAlign: "center" }}>
                  <button onClick={() => handleEdit(zone)} style={iconBtn}>
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => handleDelete(zone.zone_code)} style={{...iconBtn1,color:'#42987e'}}>
                    <Trash size={16} color="#42987e" />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <p style={{ marginTop: 10, color: "#666", textAlign: "right" }}>
        Total Highway: {filteredZones.length}
      </p>
    </div>
  );
};

/* ---------------- STYLES ---------------- */

const th = { padding: 10, textAlign: "center", borderBottom: "1px solid #eee"  };
const td = { padding: 10, borderBottom: "1px solid #eee" ,textAlign: "center" };
const iconBtn = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  marginLeft: 6,
  color:"#8b306d"
};
const iconBtn1 = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  marginLeft: 6,
  color:"#42987e"
};

/* ---------------- EXPORT ---------------- */

export default function App() {
  return (
    <AdminProvider>
      <ZoneMaster />
    </AdminProvider>
  );
}
